import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  IconButton,
  Box,
  Typography,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useQuery } from '@apollo/client/react';
import {
  GetUnpaidExpensesDocument,
  GetUnpaidExpensesQuery,
  Expense,
  MoneyTransactionFragmentFragment,
} from '../../../generated/graphql';
import { useAlert } from '@dwelvio/react-alert';
import { useApp } from '../../../components/app-context';
import { Grid } from '@mui/system';

interface ExpenseSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (expense: Expense) => void;
  transaction?: MoneyTransactionFragmentFragment | null;
}

export function ExpenseSelectionDialog({ open, onClose, onSelect, transaction }: ExpenseSelectionDialogProps) {
  const { show } = useAlert();
  const app = useApp();
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [filterMode, setFilterMode] = useState<'filtered' | 'all'>('filtered');

  const { data, loading, error } = useQuery<GetUnpaidExpensesQuery>(GetUnpaidExpensesDocument, {
    skip: !open,
  });

  React.useEffect(() => {
    if (error) {
      show.error('Nepodarilo sa načítať výdavky.', error.message);
    }
  }, [error, show]);

  const allExpenses = data?.getUnpaidExpenses ?? [];

  const expenses = React.useMemo(() => {
    if (filterMode === 'all' || !transaction) {
      return allExpenses;
    }
    return allExpenses.filter((expense) => {
      const refMatch = transaction.reference && expense.refNo && expense.refNo === transaction.reference;
      const valueMatch =
        transaction.value > 0 && Math.abs(expense.amount - transaction.value) / transaction.value <= 0.1;
      return refMatch || valueMatch;
    });
  }, [allExpenses, filterMode, transaction]);

  const columns = React.useMemo((): GridColDef[] => {
    return [
      {
        field: 'title',
        headerName: 'Názov',
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'refNo',
        headerName: 'VS',
        width: 120,
      },
      {
        field: 'amount',
        headerName: 'Suma',
        width: 100,
        valueFormatter: (value: number) => app.formatNumber(value, 2) + ' €',
      },
      {
        field: 'paidAmount',
        headerName: 'Zaplatené',
        width: 100,
        valueFormatter: (value: number) => app.formatNumber(value, 2) + ' €',
      },
      {
        field: 'remaining',
        headerName: 'Zostáva',
        width: 100,
        renderCell: (params: GridRenderCellParams<Expense>) => {
          const remaining = params.row.amount - (params.row.paidAmount || 0);
          return <span>{app.formatNumber(remaining, 2)} €</span>;
        },
      },
      {
        field: 'dueDate',
        headerName: 'Splatnosť',
        width: 120,
        valueFormatter: (value: string) => app.formatDate(value),
      },
    ];
  }, [app]);

  const handleSelect = () => {
    if (selectedExpense) {
      onSelect(selectedExpense);
      setSelectedExpense(null);
    }
  };

  const handleClose = () => {
    setSelectedExpense(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          Výber výdavku
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {transaction && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Grid container spacing={0} alignItems="center">
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="body2">
                    <strong>Dátum:</strong> {app.formatDate(transaction.transactionDate)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="body2">
                    <strong>Suma:</strong> {app.formatNumber(transaction.value, 2)} €
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  {transaction.reference && (
                    <Typography variant="body2">
                      <strong>VS:</strong> {transaction.reference}
                    </Typography>
                  )}
                </Grid>
                <Grid size={{ xs: 12 }}>
                  {transaction.note && (
                    <Typography variant="body2">
                      <strong>Poznámka:</strong> {transaction.note}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Paper>
          )}
          <Stack direction="row" justifyContent="flex-end">
            <ToggleButtonGroup
              value={filterMode}
              exclusive
              onChange={(_e, value) => value && setFilterMode(value)}
              size="small"
            >
              <ToggleButton value="filtered">Filtrované</ToggleButton>
              <ToggleButton value="all">Všetky</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Box sx={{ height: 500, width: '100%' }}>
            <DataGrid
              rows={expenses}
              columns={columns}
              loading={loading}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 50,
                  },
                },
                sorting: {
                  sortModel: [{ field: 'dueDate', sort: 'asc' }],
                },
              }}
              pageSizeOptions={[10, 25, 50]}
              onRowClick={(params) => setSelectedExpense(params.row)}
              sx={{
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                },
                '& .MuiDataGrid-row.Mui-selected': {
                  backgroundColor: 'action.selected',
                },
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Zrušiť</Button>
        <Button onClick={handleSelect} variant="contained" disabled={!selectedExpense}>
          Vybrať
        </Button>
      </DialogActions>
    </Dialog>
  );
}
